/** In-app support chat alert. Browsers only allow sound after a click/key on the page. */

type AlertKind = 'chat' | 'assign' | 'outbound'

type Tone = { freq: number; at: number; dur: number }

const CHAT_TONES: Tone[] = [
  { freq: 1244, at: 0, dur: 0.16 },
  { freq: 1661, at: 0.18, dur: 0.2 },
]
const ASSIGN_TONES: Tone[] = [
  { freq: 392, at: 0, dur: 0.16 },
  { freq: 523, at: 0.2, dur: 0.18 },
  { freq: 659, at: 0.42, dur: 0.28 },
]
/** Softer mid two-note — distinct from support chat ding and assign chime. */
const OUTBOUND_TONES: Tone[] = [
  { freq: 880, at: 0, dur: 0.18 },
  { freq: 660, at: 0.2, dur: 0.22 },
]

let chatEl: HTMLAudioElement | null = null
let assignEl: HTMLAudioElement | null = null
let outboundEl: HTMLAudioElement | null = null
let unlockBound = false
let ctx: AudioContext | null = null
let lastChatPlayAt = 0
let lastAssignPlayAt = 0
let lastOutboundPlayAt = 0

function ding(t: number, start: number, len: number, freq: number) {
  const u = t - start
  if (u < 0 || u > len) return 0
  const env = Math.exp(-u * 18) * (1 - Math.exp(-u * 90))
  const fund = Math.sin(2 * Math.PI * freq * u)
  const harm = 0.35 * Math.sin(2 * Math.PI * freq * 2 * u)
  return (fund + harm) * env
}

function wavDataUri(tones: Tone[], duration: number, gain = 0.55) {
  const sampleRate = 22050
  const n = Math.floor(sampleRate * duration)
  const dataSize = n * 2
  const buf = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buf)
  const str = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i))
  }
  str(0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  str(8, 'WAVEfmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  str(36, 'data')
  view.setUint32(40, dataSize, true)
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate
    let sample = 0
    for (const tone of tones) sample += ding(t, tone.at, tone.dur, tone.freq)
    view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, sample * gain)) * 0x7fff, true)
  }
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return `data:audio/wav;base64,${btoa(bin)}`
}

function tonesFor(kind: AlertKind): Tone[] {
  if (kind === 'assign') return ASSIGN_TONES
  if (kind === 'outbound') return OUTBOUND_TONES
  return CHAT_TONES
}

function element(kind: AlertKind) {
  if (typeof window === 'undefined') return null
  if (kind === 'assign') {
    if (!assignEl) {
      assignEl = new Audio(wavDataUri(ASSIGN_TONES, 0.78, 0.6))
      assignEl.preload = 'auto'
      assignEl.volume = 1
    }
    return assignEl
  }
  if (kind === 'outbound') {
    if (!outboundEl) {
      outboundEl = new Audio(wavDataUri(OUTBOUND_TONES, 0.5, 0.5))
      outboundEl.preload = 'auto'
      outboundEl.volume = 1
    }
    return outboundEl
  }
  if (!chatEl) {
    chatEl = new Audio(wavDataUri(CHAT_TONES, 0.42, 0.55))
    chatEl.preload = 'auto'
    chatEl.volume = 1
  }
  return chatEl
}

function webAudio() {
  if (typeof window === 'undefined') return null
  const Ctor =
    window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  if (!ctx) ctx = new Ctor()
  return ctx
}

function playOscillator(tones: Tone[]) {
  const audio = webAudio()
  if (!audio) return
  const now = audio.currentTime
  for (const tone of tones) {
    const osc = audio.createOscillator()
    const gain = audio.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(tone.freq, now + tone.at)
    const t0 = now + tone.at
    gain.gain.setValueAtTime(0.0001, t0)
    gain.gain.exponentialRampToValueAtTime(0.16, t0 + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + tone.dur)
    osc.connect(gain)
    gain.connect(audio.destination)
    osc.start(t0)
    osc.stop(t0 + tone.dur)
  }
}

function unlockEl(el: HTMLAudioElement | null) {
  if (!el) return
  el.muted = true
  void el
    .play()
    .then(() => {
      el.pause()
      el.currentTime = 0
      el.muted = false
    })
    .catch(() => {
      el.muted = false
    })
}

function unlock() {
  const audio = webAudio()
  if (audio && audio.state === 'suspended') void audio.resume()
  unlockEl(element('chat'))
  unlockEl(element('assign'))
  unlockEl(element('outbound'))
}

function playAlert(kind: AlertKind) {
  if (typeof window === 'undefined') return
  const tones = tonesFor(kind)
  const el = element(kind)
  const audio = webAudio()
  if (audio && audio.state === 'suspended') void audio.resume()
  if (el) {
    el.muted = false
    try {
      el.currentTime = 0
    } catch {
      /* ignore */
    }
    void el.play().catch(() => {
      if (audio?.state === 'running') playOscillator(tones)
    })
    return
  }
  if (audio?.state === 'running') playOscillator(tones)
}

/** Must run while the console is open so the first click/key unlocks sound. */
export function armSupportChatBeep() {
  if (unlockBound || typeof window === 'undefined') return
  unlockBound = true
  element('chat')
  element('assign')
  element('outbound')
  const onGesture = () => unlock()
  window.addEventListener('pointerdown', onGesture, { capture: true, passive: true })
  window.addEventListener('keydown', onGesture, { capture: true })
}

export function playSupportChatBeep() {
  const nowMs = Date.now()
  if (nowMs - lastAssignPlayAt < 1400) return
  if (nowMs - lastChatPlayAt < 700) return
  lastChatPlayAt = nowMs
  playAlert('chat')
}

/** Lower rising chime for the person who was just assigned the ticket. */
export function playSupportAssignBeep() {
  const nowMs = Date.now()
  if (nowMs - lastAssignPlayAt < 900) return
  lastAssignPlayAt = nowMs
  playAlert('assign')
}

/** Soft mid two-note for Our Drivers outbound DM replies. */
export function playOutboundDmBeep() {
  const nowMs = Date.now()
  if (nowMs - lastOutboundPlayAt < 900) return
  lastOutboundPlayAt = nowMs
  playAlert('outbound')
}

export function unreadMessageTotal(counts: Record<string, number>) {
  return Object.values(counts).reduce((n, c) => n + (Number(c) || 0), 0)
}
